/**
 * Panel Chat — console opérateur (SolidJS).
 *
 * Chat bidirectionnel entre l'opérateur et l'utilisateur assisté (tray client),
 * avec :
 *   - bouton trombone 📎 pour envoyer des fichiers (protocole file:* + cadres FB),
 *   - section "Assistant IA" qui suggère des réponses via une session CLI
 *     (Claude/Codex/Kimi/Gemini) déjà disponible sur le host.
 *
 * Protocole chat : message WebSocket `{ type: "chat", text }` diffusé à toute la
 * session par le relay Rust. Côté host, MessageRouter.OnChat pousse au tray.
 *
 * Protocole fichier : `file:*` + cadres `FB` binaires (cf. shared/file-transfer.ts).
 * L'Agent dépose les fichiers reçus dans %ProgramData%\RemoteWS\downloads.
 *
 * IA : réutilise les tools `cli_start` / `cli_send_message` existants et écoute
 * les events `live/cli/event { kind: "assistant_msg" }` pour capturer la réponse.
 */
import { For, Show, createMemo, createSignal, onCleanup, onMount, type Component } from "solid-js";
import { PanelLayout } from "@/shared/panel-base";
import { useWs } from "@/shared/ws";
import { useToolClient } from "@/shared/tool-client";
import { registerPanel } from "@/panel-registry";
import { uploadFile, type UploadProgress, fmtBytes } from "@/shared/file-transfer";
import { isWsEnvelope } from "@/shared/ws-protocol";
import "./chat.css";

interface ChatLine {
  id: string;
  from: "me" | "them" | "ai";
  text: string;
  at: number;
  fileName?: string;
  aiSuggestion?: boolean;
}

const ChatPanel: Component = () => {
  const ws = useWs();
  const client = useToolClient();
  const [messages, setMessages] = createSignal<ChatLine[]>([]);
  const [input, setInput] = createSignal("");
  const [connected, setConnected] = createSignal(false);
  const [uploads, setUploads] = createSignal<UploadProgress[]>([]);
  let fileInput: HTMLInputElement | undefined;

  // ── Chat entrant ──────────────────────────────────────────────────────────
  const offChat = ws.on(
    (m) => m.type === "chat",
    (m) => {
      const msg = m as { type: "chat"; text: string; fromPeerId?: string };
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          from: msg.fromPeerId === ws.state.myPeerId ? "me" : "them",
          text: msg.text,
          at: Date.now(),
        },
      ]);
      scrollDown();
    },
  );

  // ── Statut de session ─────────────────────────────────────────────────────
  const offStatus = ws.on(
    (m) => m.type === "session:joined" || m.type === "session:created" || m.type === "session:state",
    () => setConnected(true),
  );
  onMount(() => {
    if (ws.state.sessionCode) setConnected(true);
    // Annoncer la capacité fileBinary pour que l'Agent sache qu'on accepte les FB.
    ws.send({ type: "operator:capabilities", capabilities: { fileBinary: true } });
  });

  // ── Fichier : handlers file:* (réponses de l'Agent aux uploads) ────────────
  const offFile = ws.on(
    (m) => typeof m.type === "string" && m.type.startsWith("file:"),
    (m) => {
      const msg = m as { type: string; fileId?: string; ackedOffset?: number; ok?: boolean; error?: string };
      setUploads((prev) => {
        const idx = prev.findIndex((u) => u.fileName === msg.fileId);
        if (msg.type === "file:resume" && msg.ackedOffset !== undefined && idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], status: "uploading", bytesTransferred: msg.ackedOffset };
          return next;
        }
        if (msg.type === "file:complete" && idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], status: "done", percent: 100 };
          // Message de confirmation dans le chat.
          setMessages((p) => [
            ...p,
            { id: crypto.randomUUID(), from: "me", text: "", at: Date.now(), fileName: next[idx].fileName },
          ]);
          setTimeout(() => setUploads((p) => p.filter((_, i) => i !== idx)), 3000);
          return next;
        }
        if (msg.type === "file:error" && idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], status: "error", error: msg.error };
          return next;
        }
        return prev;
      });
    },
  );

  // ── IA : session CLI + suggestion ─────────────────────────────────────────
  const [aiCli, setAiCli] = createSignal<string>("claude");
  const [aiSessionId, setAiSessionId] = createSignal<string | null>(null);
  const [aiSuggestion, setAiSuggestion] = createSignal<string>("");
  const [aiThinking, setAiThinking] = createSignal(false);
  const [aiErr, setAiErr] = createSignal("");

  // Écoute des réponses assistant_msg de la session CLI.
  const offAi = ws.on(
    (m) => isWsEnvelope(m, "live", "cli", "event"),
    (m) => {
      const env = m as { op?: string; payload?: { kind?: string; content?: string | null; sessionId?: string } };
      const p = env.payload;
      if (!p || p.sessionId !== aiSessionId()) return;
      if (p.kind === "assistant_msg" && p.content) {
        setAiSuggestion(p.content);
        setAiThinking(false);
      } else if (p.kind === "error") {
        setAiErr(p.content || "Erreur CLI");
        setAiThinking(false);
      }
    },
  );

  onCleanup(() => {
    offChat();
    offStatus();
    offFile();
    offAi();
  });

  // ── Envoi chat ────────────────────────────────────────────────────────────
  const send = () => {
    const text = input().trim();
    if (!text) return;
    ws.send({ type: "chat", text });
    setMessages((prev) => [...prev, { id: crypto.randomUUID(), from: "me", text, at: Date.now() }]);
    setInput("");
    scrollDown();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // ── Fichier : upload ──────────────────────────────────────────────────────
  const onFileSelected = (e: Event) => {
    const target = e.target as HTMLInputElement;
    if (!target.files || target.files.length === 0) return;
    const file = target.files[0];
    target.value = ""; // reset pour permettre de re-sélectionner le même fichier

    const { promise } = uploadFile({
      ws,
      file,
      onProgress: (p) => {
        setUploads((prev) => {
          const idx = prev.findIndex((u) => u.fileName === p.fileName);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = p;
            return next;
          }
          return [...prev, p];
        });
      },
    });
    promise.catch((err) => console.error("[chat] upload failed", err));
  };

  const lastUserMessage = createMemo(() => {
    const msgs = messages();
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].from === "them") return msgs[i].text;
    }
    return null;
  });

  const askAi = async () => {
    const userMsg = lastUserMessage();
    if (!userMsg) {
      setAiErr("Aucun message client à traiter.");
      return;
    }
    setAiErr("");
    setAiSuggestion("");
    setAiThinking(true);

    try {
      // Démarrer une session CLI si aucune n'est active.
      let sess = aiSessionId();
      if (!sess) {
        const r = (await client.call(
          "cli_start",
          { cli: aiCli() },
          {
            userConfirmedAt: new Date().toISOString(),
          },
        )) as { sessionId: string };
        sess = r.sessionId;
        setAiSessionId(sess);
      }

      // Construire le prompt avec contexte de support.
      const prompt = `Tu es un assistant de support informatique. Un utilisateur demande de l'aide. Réponds de manière concise et utile en français.\n\nMessage de l'utilisateur : "${userMsg}"`;
      await client.call("cli_send_message", { sessionId: sess, text: prompt });
      // La réponse arrive via l'event live/cli/event — capturée par le handler ws.on ci-dessus.
    } catch (e: unknown) {
      setAiErr(e instanceof Error ? e.message : String(e));
      setAiThinking(false);
    }
  };

  const sendAiSuggestion = () => {
    const text = aiSuggestion().trim();
    if (!text) return;
    ws.send({ type: "chat", text });
    setMessages((prev) => [...prev, { id: crypto.randomUUID(), from: "me", text, at: Date.now() }]);
    setAiSuggestion("");
    scrollDown();
  };

  function scrollDown() {
    setTimeout(() => {
      const el = document.getElementById("chat-scroll");
      if (el) el.scrollTop = el.scrollHeight;
    }, 0);
  }

  return (
    <PanelLayout title="Chat avec l'utilisateur">
      <div class="chat-panel">
        {/* Indicateur de connexion */}
        <div class="chat-status">
          <span classList={{ "chat-dot": true, connected: connected(), disconnected: !connected() }} />
          <span>{connected() ? "Session active" : "En attente de session…"}</span>
        </div>

        {/* Liste des messages */}
        <div id="chat-scroll" class="chat-messages">
          <Show when={messages().length === 0}>
            <div class="chat-empty">Aucun message. Envoyez un premier message à l'utilisateur.</div>
          </Show>
          <For each={messages()}>
            {(m) => (
              <div classList={{ "chat-line": true, mine: m.from === "me", theirs: m.from === "them" }}>
                <Show when={m.fileName}>
                  <div class="chat-file">📎 {m.fileName}</div>
                </Show>
                <Show when={!m.fileName}>
                  <div class="chat-bubble">{m.text}</div>
                </Show>
                <div class="chat-time">
                  {new Date(m.at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            )}
          </For>

          {/* Uploads en cours */}
          <For each={uploads()}>
            {(u) => (
              <div class="chat-line mine">
                <div class="chat-upload">
                  <span>📎 {u.fileName}</span>
                  <Show when={u.status === "uploading" || u.status === "offering"}>
                    <progress value={u.percent} max="100" style={{ width: "100%", height: "4px" }} />
                    <span class="chat-upload-pct">
                      {u.percent}% · {fmtBytes(u.bytesTransferred)}
                    </span>
                  </Show>
                  <Show when={u.status === "done"}>
                    <span class="chat-upload-ok">✓ Envoyé</span>
                  </Show>
                  <Show when={u.status === "error"}>
                    <span class="chat-upload-err">✗ {u.error}</span>
                  </Show>
                </div>
              </div>
            )}
          </For>
        </div>

        {/* Section IA */}
        <div class="chat-ai">
          <div class="chat-ai-header">
            <span>🤖 Assistant IA</span>
            <select value={aiCli()} onChange={(e) => setAiCli(e.currentTarget.value)} class="chat-ai-select">
              <option value="claude">Claude</option>
              <option value="codex">Codex</option>
              <option value="kimi">Kimi</option>
              <option value="gemini">Gemini</option>
              <option value="hermes">Hermes</option>
            </select>
            <button class="chat-ai-btn" onClick={askAi} disabled={aiThinking() || !lastUserMessage()}>
              {aiThinking() ? "…" : "Suggérer"}
            </button>
          </div>
          <Show when={aiErr()}>
            <div class="chat-ai-err">{aiErr()}</div>
          </Show>
          <Show when={aiSuggestion()}>
            <div class="chat-ai-suggestion">
              <div class="chat-ai-suggestion-text">{aiSuggestion()}</div>
              <button class="chat-ai-send" onClick={sendAiSuggestion}>
                Envoyer
              </button>
            </div>
          </Show>
        </div>

        {/* Zone de saisie + trombone */}
        <div class="chat-input-row">
          <input ref={fileInput} type="file" style={{ display: "none" }} onChange={onFileSelected} />
          <button class="chat-attach" title="Joindre un fichier" onClick={() => fileInput?.click()}>
            📎
          </button>
          <textarea
            class="chat-input"
            placeholder="Tapez un message… (Entrée pour envoyer, Maj+Entrée pour un saut de ligne)"
            value={input()}
            onInput={(e) => setInput(e.currentTarget.value)}
            onKeyDown={onKeyDown}
            rows={2}
          />
          <button class="chat-send" onClick={send} disabled={!input().trim()}>
            Envoyer
          </button>
        </div>
      </div>
    </PanelLayout>
  );
};

registerPanel("chat", ChatPanel);
export default ChatPanel;
